import React, { ReactNode, useRef, } from "react";
import { forwardRefPolymorphic } from "@mrocket/utils";
import { Spinner } from "../Spinner/Spinner";
import { getIconComponent, IconType } from "../Icon";
import { Align, HTMLBaseSystemProps, SizeName } from "@mrocket/styles";
import { ButtonAfterContent, ButtonBeforeContent, ButtonContent, ButtonRoot } from "./Button.styles";


export interface ButtonProps extends HTMLBaseSystemProps<'button'> {

  size?: SizeName
  align?: Align
  stretched?: boolean
  after?: ReactNode
  before?: ReactNode
  icon?: IconType
  iconLeft?: IconType
  iconRight?: IconType
  progress?: boolean;
  hasMinWidth?: boolean
}

/**
 * Polymorphic render button
 * */
export const Button = forwardRefPolymorphic<'button', ButtonProps>((
  {
    children,
    type = 'button',
    align = 'center',
    mode = 'secondary',
    appearance = 'default',
    radius = 'rounded',
    progress,
    disabled,
    className,
    after,
    before,
    icon,
    iconRight,
    iconLeft,
    hasMinWidth = true,
    ...props
  },
  ref
) => {


  const contentRef = useRef<HTMLDivElement>(null)
  const showChildren = Boolean(children) && children !== true
  const iconLeftOrIcon = iconLeft || icon

  const initProps = {
    ...props,
    ref,
    type,
    disabled: Boolean(progress || disabled),
    className,
    hasMinWidth,
    mode,
    appearance,
    radius,
    ['aria-disabled']: disabled,
    ['aria-busy']: progress,
    style: progress ? {...props.style, width: contentRef.current?.offsetWidth} : props.style
  }

  const singleIconProps = {
    singleIcon: !showChildren
  }

  if (progress) {
    return <ButtonRoot  {...initProps} {...singleIconProps}><Spinner size={props.size}/></ButtonRoot>
  }


  return (
    <ButtonRoot {...initProps} {...singleIconProps}>
      {before && <ButtonBeforeContent {...singleIconProps}>{before}</ButtonBeforeContent>}
      {iconLeftOrIcon &&
        <ButtonBeforeContent {...singleIconProps}>
          <>{getIconComponent(iconLeftOrIcon)}</>
        </ButtonBeforeContent>
      }
      {showChildren && (
        <ButtonContent
          ref={contentRef}
          right={Boolean(iconRight)}
          left={Boolean(iconLeftOrIcon)}
          align={align}
        >
          {children}
        </ButtonContent>
      )}

      {iconRight &&
        <ButtonAfterContent {...singleIconProps}>
          <>{getIconComponent(iconRight)}</>
        </ButtonAfterContent>
      }
      {after && <ButtonAfterContent {...singleIconProps}>{after}</ButtonAfterContent>}
    </ButtonRoot>
  )
});

Button.displayName = 'Button'






